import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  const connect = useCallback(() => {
    if (!isAuthenticated || !user?.id) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}`;
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.id}`);

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch (error) {
        console.error('WebSocket message parse error:', error);
      }
    };

    ws.onclose = () => {
      setConnected(false);
    };

    ws.onerror = () => {
      // Will trigger onclose
    };

    socketRef.current = ws;
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    connect();
    return () => {
      if (socketRef.current) socketRef.current.close();
    };
  }, [connect]);

  // Reconnect on disconnect
  useEffect(() => {
    if (!connected && isAuthenticated && user?.id) {
      const timer = setTimeout(connect, 5000);
      return () => clearTimeout(timer);
    }
  }, [connected, isAuthenticated, user?.id, connect]);

  const sendMessage = useCallback((message) => {
    if (socketRef.current && connected) {
      socketRef.current.send(JSON.stringify(message));
    }
  }, [connected]);

  const value = {
    connected,
    lastMessage,
    sendMessage
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
